<template>
  <dialog
    ref="dialogEl"
    class="scrim"
    aria-labelledby="cards-restored-title"
    @cancel.prevent="emit('close')"
    @click="onScrimClick"
  >
    <div class="panel">
      <div class="head">
        <div>
          <p class="kicker">{{ t('mailbox.team') }}</p>
          <h2 id="cards-restored-title">{{ t('home.cardsRestoredTitle') }}</h2>
        </div>
        <CloseXButton :label="t('mailbox.close')" @click="emit('close')" />
      </div>
      <div class="body">
        <p class="message">{{ message }}</p>
      </div>
      <div class="actions">
        <button type="button" class="cta" @click="emit('close')">
          {{ t('mailbox.gotIt') }}
        </button>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from '../i18n/t'
import CloseXButton from './CloseXButton.vue'

const props = defineProps<{
  copies: number
}>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const dialogEl = ref<HTMLDialogElement | null>(null)

const message = computed(() =>
  props.copies === 1
    ? t('home.cardsRestoredOne')
    : t('home.cardsRestoredMany', { n: props.copies }),
)

onMounted(() => {
  dialogEl.value?.showModal()
})

function onScrimClick(event: MouseEvent): void {
  if (event.target === dialogEl.value) emit('close')
}
</script>

<style scoped>
.scrim {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  padding: max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  border: 0;
  background: transparent;
  color: var(--md-sys-color-on-surface);
  overflow: hidden;
  animation: restore-in var(--md-motion-medium) var(--md-motion-standard);
}

.scrim::backdrop {
  background: var(--md-scrim);
  backdrop-filter: blur(10px);
}

.panel {
  display: flex;
  flex-direction: column;
  width: min(480px, 100%);
  max-height: min(72vh, 640px);
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  border: 1px solid color-mix(in srgb, var(--md-sys-color-primary) 40%, transparent);
  box-shadow: var(--md-elevation-2);
  color: var(--md-sys-color-on-surface);
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 16px 12px 24px;
}

.kicker {
  margin: 0 0 4px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  font-size: 28px;
  font-weight: 400;
  line-height: 36px;
}

.body {
  overflow: auto;
  overscroll-behavior: contain;
  padding: 4px 24px 8px;
}

.message {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px 16px;
}

.cta {
  min-height: 40px;
  padding: 8px 16px;
  border: 0;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.cta:hover {
  filter: brightness(1.06);
}

@keyframes restore-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .scrim {
    animation: none;
  }

  .scrim::backdrop {
    backdrop-filter: none;
  }
}
</style>
